import React, { useEffect, useState, useCallback, Fragment } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import styled, { css } from 'styled-components';
import { get, slice } from 'lodash';

import {
  AlertErrorNotification,
  AlertSuccess,
  AlertWarning,
  AlertInfo,
} from '@ad-scripts/presentation/components/common/alert';
import { Button, Tooltip } from 'src/presentation/components/agent_desktop_components';
import httpLogsResource from 'src/actions/httpLogs/httpLogsResource';
import LogDetailsTable from './components/LogDetailsTable';
import { LogLevel } from 'src/actions/httpLogs/httpLogsTypes';
import SharedTable, { Row } from 'src/presentation/components/pages/shared/Table';
import { Spinner } from 'src/presentation/components/pages/shared/Spinner';
import { StyledChevronUpIcon, StyledChevronDownIcon } from 'src/presentation/components/pages/ivrLogs/ivrLogsLayout';
import { toastError } from 'src/actions/toast';
import { TR } from '@ad-scripts/presentation/components/common/table';
import useResource, { isLoadingStatus, STATUS_SUCCEEDED, STATUS_FAILED } from 'src/presentation/hooks/useResource';

const ID_FIELD = 'id';

const columnConfig = ({ rowsState, toggleState }) => [
  {
    attr: 'level',
    label: 'Level',
    customColumnStyle: () => ({ width: '10px', color: 'black' }),
    customRenderer: log => {
      switch (log.level) {
        case LogLevel.INFO:
          return (
            <Tooltip message="Info" style={{ marginLeft: '-10px' }}>
              <StyledAlertSuccess dataAid={'alert-success-icon'} />
            </Tooltip>
          );
        case LogLevel.DEBUG:
          return (
            <Tooltip message="Debug" style={{ marginLeft: '-10px' }}>
              <StyledAlertWarning dataAid={'alert-warning-icon'} />
            </Tooltip>
          );
        case LogLevel.ERROR:
          return (
            <Tooltip message="Error" style={{ marginLeft: '-10px' }}>
              <StyledAlertError dataAid={'alert-error-icon'} />
            </Tooltip>
          );
        default:
          return (
            <Tooltip>
              <StyledAlertInfo />
            </Tooltip>
          );
      }
    },
  },
  {
    attr: 'metadata.eventType',
    label: 'Event Type',
    customColumnStyle: () => ({ width: '200px', color: 'black' }),
  },
  {
    attr: 'sentAt',
    label: 'Occurred At',
    customColumnStyle: () => ({ width: '150px', color: 'black' }),
    customRenderer: log => {
      return <div>{moment(log.sentAt).format('hh:mm:ss.SS A MM/DD/YY')}</div>;
    },
  },
  {
    attr: 'collapsible',
    customColumnStyle: () => ({ width: '16px' }),
    customRenderer: row => {
      const rowId = get(row, ID_FIELD);
      const expanded = !!(rowId && rowsState[rowId]);

      return (
        <div id={`expand-log-button-${rowId}`} data-aid={'expand-log-button'} onClick={() => toggleState(rowId)}>
          {expanded ? <StyledChevronUpIcon /> : <StyledChevronDownIcon />}
        </div>
      );
    },
  },
];

const WebhookLogs = ({ webhookId }): JSX.Element | null => {
  const dispatch = useDispatch();
  const {
    values: logs,
    loadingState,
    errors,
  } = useResource(httpLogsResource, {
    fetch: true,
    query: { requestType: 'WEBHOOK', webhookId: webhookId },
  });

  const [shownItems, setShownItems] = useState(Math.min(logs.length, 10));
  const { rowsState, toggleState } = useCollapsibleRows();
  const outerColumns = columnConfig({ rowsState, toggleState });
  const isLoading = isLoadingStatus(loadingState);

  const updateShownItems = useCallback(() => {
    setShownItems(currentShownItems => Math.min(logs.length, currentShownItems + 10));
  }, [logs.length]);

  useEffect(() => {
    // Once we have loaded the data, refresh the row counts to display the first page
    if (loadingState === STATUS_SUCCEEDED && logs.length && !shownItems) {
      updateShownItems();
    } else if (loadingState === STATUS_FAILED) {
      dispatch(toastError('Error while loading data. Please wait and try refreshing the page'));
    }
  }, [loadingState, updateShownItems, dispatch, logs.length, shownItems]);

  function renderLogRow(log, index) {
    const clickExpandButton = index => {
      document.getElementById(`expand-log-button-${index}`)?.click();
    };
    const outerRowName = `outer-log-row-${index % 2 ? 'odd' : 'even'}`;
    return (
      <Fragment key={index}>
        <OuterRow
          columnConfig={outerColumns}
          item={log}
          renderRowActions={() => null}
          rowDataAid={outerRowName}
          className={outerRowName}
          onClick={() => clickExpandButton(get(log, ID_FIELD))}
        />
        {rowsState[get(log, ID_FIELD)] ? (
          <TR>
            <TableCell colSpan={outerColumns.length + 1}>
              <LogDetailsTable item={log} />
            </TableCell>
          </TR>
        ) : (
          <CollapsedRow data-aid={'collapsed-row'} />
        )}
      </Fragment>
    );
  }

  function renderShowMoreButton() {
    if (shownItems < logs.length) {
      return (
        <StyledButton
          data-aid={`show-more-logs-button`}
          isSmall
          type={Button.Types.TEXT}
          onClick={() => updateShownItems()}
        >
          Show More
        </StyledButton>
      );
    }
  }

  if (isLoading) {
    return (
      <StyledTableContent>
        <Spinner />
      </StyledTableContent>
    );
  }

  if (errors && errors.length > 0) {
    return (
      <StyledTableContent>
        <ErrorText data-aid={'logs-error'}>There was an error while fetching logs. Reload the page to retry.</ErrorText>
      </StyledTableContent>
    );
  }

  if (!isLoading && logs.length === 0) {
    return (
      <StyledTableContent>
        <NoLogsRow data-aid={'logs-empty'}>
          No logs are available. Logs will appear here once one of the events on this webhook has been triggered.
        </NoLogsRow>
      </StyledTableContent>
    );
  }

  return (
    <TableContentWrapper>
      <StyledTableContent>
        <>
          <OuterTable
            key={'log-outer-table'}
            dataAid={'outer-log'}
            columnConfig={outerColumns}
            items={slice(logs, 0, shownItems)}
            tabRenderRow={renderLogRow}
            isLoading={isLoading}
          />
          <ButtonRow data-aid={'show-more-logs-row'}>
            <ButtonContainer>
              {shownItems} of {logs.length}
              {renderShowMoreButton()}
            </ButtonContainer>
          </ButtonRow>
        </>
      </StyledTableContent>
    </TableContentWrapper>
  );
};

const useCollapsibleRows = () => {
  const [rowsState, setRowsState] = useState({});
  const toggleState = useCallback(
    id => {
      const newState = {
        ...rowsState,
        [id]: !rowsState[id],
      };
      setRowsState(newState);
    },
    [setRowsState, rowsState]
  );

  return { rowsState, toggleState };
};

const ButtonRow = styled.div`
  width: 100%;
  height: 35px;
  background-color: ${p => p.theme.colors.white};
`;

const ButtonContainer = styled.div`
  float: left;
  margin: 6px;
`;

const ErrorText = styled.div`
  color: ${p => p.theme.colors.red500};
`;

const NoLogsRow = styled.div`
  color: ${p => p.theme.colors.gray700};
  display: flex;
  height: 50px;
  width: 100%;
`;

const OuterRow = styled(Row)`
  background-color: ${props => props.theme.colors.white};
`;

const OuterTable = styled(SharedTable)`
  background-color: ${p => p.theme.colors.white};

  .outer-log-row-odd {
    cursor: pointer;
    background-color: ${p => p.theme.colors.gray200};
  }

  .outer-log-row-even {
    cursor: pointer;
    background-color: ${p => p.theme.colors.white};
  }
`;

const StyledButton = styled(Button)`
  color: ${p => p.theme.colors.gray700};
`;

const StyledTableContent = styled.div`
  max-width: 800px;
  width: 100%;
  padding-top: 0;
`;

const TableContentWrapper = styled.div`
  width: 650px;
`;

const BaseAlert = css`
  background-color: transparent;
  padding: 8px;
  width: 16px;
  height: 32px;
  min-height: 16px;

  &.styled-alerticon {
    margin-right: 8px;
  }
`;

const StyledAlertSuccess = styled(AlertSuccess)`
  ${BaseAlert}
`;

const StyledAlertError = styled(AlertErrorNotification)`
  ${BaseAlert}
`;

const StyledAlertWarning = styled(AlertWarning)`
  ${BaseAlert}
`;

const StyledAlertInfo = styled(AlertInfo)`
  ${BaseAlert}
`;

const TableCell = styled.td`
  padding: 0;
  background-color: ${p => p.theme.colors.white};
`;

const CollapsedRow = styled(TR)`
  height: 0;
`;

export default WebhookLogs;
